import React from 'react';
import { formatVehicleBrandModel } from '../../utils/vehicleBrand';
import {
  Box,
  Flex,
  Text,
  Badge,
  Button,
  SimpleGrid,
  HStack,
  Select,
  FormLabel,
  useColorModeValue,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  primaryButtonProps,
  secondaryButtonProps,
  getStatusBadgeProps,
  warningButtonProps,
  alertBadgeProps,
} from '../../theme/adminUi';
import {
  getWarrantyTypeLabel,
  isWarrantyServiceType,
} from '../../utils/warrantyTypes';

const InfoLine = ({ label, value }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" mb={0.5}>
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={2}>
      {value || '—'}
    </Text>
  </Box>
);

const SchedulerServiceHero = ({
  appointment,
  vehicleService = null,
  start,
  end,
  onOpenHistory,
  onOpenAlerts,
  hasAlerts,
  loadingHistory,
  onAssociateLastReception,
  associatingLastReception = false,
  showAssociateLastReception = false,
  assistant,
  setAssistant,
  mechanicsList = [],
  mechanicId,
  onMechanicChange,
  showStaffEditor = false,
  assistantName,
}) => {
  const vehicle = appointment?.vehicle;
  const customer = appointment?.customer;
  const status = appointment?.status;
  const warrantyTypeCode =
    vehicleService?.warrantyType ||
    appointment?.warrantyType ||
    appointment?.vehicleService?.warrantyType ||
    '';
  const showWarrantyType =
    isWarrantyServiceType(
      appointment?.serviceType ||
        appointment?.serviceTypeId ||
        vehicleService?.serviceType ||
        vehicleService?.serviceTypeId,
    ) && Boolean(warrantyTypeCode);
  const warrantyTypeLabel = getWarrantyTypeLabel(warrantyTypeCode);

  const currentMechanicId = Number(
    appointment?.mechanicId || mechanicId || 0,
  );

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const bg = useColorModeValue('white', 'navy.800');

  const handleMechanicSelect = (e) => {
    const id = Number(e.target.value);
    const mechanic = mechanicsList.find((m) => Number(m.id) === id) || null;
    onMechanicChange?.(id, mechanic);
    if (setAssistant && Number(assistant) === id) {
      setAssistant('0');
    }
  };

  return (
    <Box
      bg={bg}
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
      p={{ base: 4, md: 5 }}
      mb={4}
    >
      <Flex
        justify="space-between"
        align="flex-start"
        gap={4}
        flexDirection={{ base: 'column', md: 'row' }}
      >
        <Box flex={1} minW={0}>
          <HStack spacing={2} mb={2} flexWrap="wrap">
            {status && <Badge {...getStatusBadgeProps(status)}>{status}</Badge>}
            {hasAlerts && <Badge {...alertBadgeProps}>Avisos pendientes</Badge>}
            <Text fontSize="xs" color="gray.500">
              Orden #{appointment?.id}
            </Text>
          </HStack>

          <Text fontSize="md" fontWeight="semibold" color="gray.900" mb={1}>
            {appointment?.service?.name || appointment?.description || 'Servicio'}
          </Text>

          {(appointment?.serviceType?.name || showWarrantyType) && (
            <Text fontSize="sm" color="gray.500" mb={2}>
              {[
                appointment?.serviceType?.name,
                showWarrantyType ? warrantyTypeLabel : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          )}

          {start && (
            <Text fontSize="sm" color="gray.600">
              {format(start, "EEEE d 'de' MMMM · HH:mm", { locale: es })}
              {end && ` → ${format(end, 'HH:mm')}`}
            </Text>
          )}

          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={4}>
            <InfoLine label="Cliente" value={customer?.name} />
            <InfoLine
              label="Vehículo"
              value={`${vehicle?.plate || '—'} · ${formatVehicleBrandModel(vehicle)}`}
            />
            {showWarrantyType && (
              <InfoLine label="Tipo de garantía" value={warrantyTypeLabel} />
            )}
            <InfoLine
              label="Kilómetros"
              value={
                vehicle?.kilometers === 0 || vehicle?.kilometers
                  ? String(vehicle.kilometers)
                  : ''
              }
            />
            <InfoLine label="Chasis" value={vehicle?.chassisNumber} />
            <InfoLine label="Número de motor" value={vehicle?.engineNumber} />
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Mecánico / Ayudante
              </Text>
              {showStaffEditor && onMechanicChange ? (
                <SimpleGrid columns={2} spacing={2}>
                  <Box>
                    <FormLabel fontSize="xs" mb={1} color="gray.500">
                      Mecánico
                    </FormLabel>
                    <Select
                      size="sm"
                      value={currentMechanicId ? String(currentMechanicId) : ''}
                      onChange={handleMechanicSelect}
                    >
                      <option value="" disabled>
                        Seleccionar
                      </option>
                      {mechanicsList?.map((m) => (
                        <option key={m.id} value={String(m.id)}>
                          {m.name}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box>
                    <FormLabel fontSize="xs" mb={1} color="gray.500">
                      Ayudante
                    </FormLabel>
                    <Select
                      size="sm"
                      value={String(assistant ?? 0)}
                      onChange={(e) => setAssistant?.(e.target.value)}
                      isDisabled={!setAssistant}
                    >
                      <option value="0">Sin ayudante</option>
                      {mechanicsList
                        ?.filter((m) => Number(m.id) !== currentMechanicId)
                        .map((m) => (
                          <option key={m.id} value={String(m.id)}>
                            {m.name}
                          </option>
                        ))}
                    </Select>
                  </Box>
                </SimpleGrid>
              ) : (
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                  {[appointment?.mechanic?.name, assistantName].filter(Boolean).join(' · ') ||
                    '—'}
                </Text>
              )}
            </Box>
            <InfoLine
              label="Duración"
              value={
                appointment?.estimatedTime
                  ? `${appointment.estimatedTime} min`
                  : '—'
              }
            />
            <InfoLine
              label="Tipo de traslado"
              value={appointment?.customerTransfer?.name}
            />
          </SimpleGrid>
        </Box>

        <Flex gap={2} flexShrink={0} flexWrap="wrap">
          <Button
            {...(hasAlerts ? warningButtonProps : secondaryButtonProps)}
            onClick={onOpenAlerts}
          >
            Avisos
          </Button>
          {showAssociateLastReception && onAssociateLastReception && (
            <Button
              {...secondaryButtonProps}
              onClick={onAssociateLastReception}
              isLoading={associatingLastReception}
            >
              Asociar a última recepción
            </Button>
          )}
          <Button
            {...primaryButtonProps}
            onClick={onOpenHistory}
            isLoading={loadingHistory}
          >
            Historial
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default SchedulerServiceHero;
